import React, { useCallback, useMemo, useState } from 'react'
import { defineMessages, IntlShape, useIntl } from 'react-intl'
import { IconButton, LeafIcon } from '@thriveglobal/thrive-web-leafkit'

export type FeedbackActionsProps = {
    onFeedback: (feedback: boolean) => void
    title?: string
}

export const messages = defineMessages({
    givePositiveFeedback: {
        defaultMessage: 'Give positive feedback',
        description: 'Aria label for positive feedback action'
    },
    givePositiveFeedbackPressed: {
        defaultMessage: 'Positive feedback given, press to remove',
        description: 'Aria label for positive feedback action when pressed'
    },
    givePositiveFeedbackWithTitle: {
        defaultMessage: 'Give positive feedback for "{articleTitle}"',
        description: 'Aria label for positive feedback action with title'
    },
    givePositiveFeedbackPressedWithTitle: {
        defaultMessage:
            'Positive feedback given for "{articleTitle}", press to remove',
        description:
            'Aria label for positive feedback action when pressed with title'
    },
    giveNegativeFeedback: {
        defaultMessage: 'Give Negative feedback',
        description: 'Aria label for negative feedback action'
    },
    giveNegativeFeedbackPressed: {
        defaultMessage: 'Negative feedback given, press to remove',
        description: 'Aria label for negative feedback action when pressed'
    },
    giveNegativeFeedbackWithTitle: {
        defaultMessage: 'Give Negative feedback for "{articleTitle}"',
        description: 'Aria label for negative feedback action with title'
    },
    giveNegativeFeedbackPressedWithTitle: {
        defaultMessage:
            'Negative feedback given for "{articleTitle}", press to remove',
        description: 'Aria label for negative feedback action when pressed'
    }
})

type FeedbackType = 'positive' | 'negative'

function getAriaLabelForPressedState({
    title,
    feedbackType
}: {
    title?: string
    feedbackType: FeedbackType
}) {
    if (feedbackType === 'positive') {
        return title
            ? messages.givePositiveFeedbackPressedWithTitle
            : messages.givePositiveFeedbackPressed
    } else {
        return title
            ? messages.giveNegativeFeedbackPressedWithTitle
            : messages.giveNegativeFeedbackPressed
    }
}

function getAriaLabelForUnpressedState({
    title,
    feedbackType
}: {
    title?: string
    feedbackType: FeedbackType
}) {
    if (feedbackType === 'positive') {
        return title
            ? messages.givePositiveFeedbackWithTitle
            : messages.givePositiveFeedback
    } else {
        return title
            ? messages.giveNegativeFeedbackWithTitle
            : messages.giveNegativeFeedback
    }
}

function FeedbackActions(props: FeedbackActionsProps): JSX.Element {
    const { onFeedback, title } = props
    const { formatMessage } = useIntl()

    const [positiveFeedback, setPositiveFeedback] = useState<boolean | null>(
        null
    )
    const [negativeFeedback, setNegativeFeedback] = useState<boolean | null>(
        null
    )

    const handlePositiveFeedback = useCallback(() => {
        const newFeedback = !positiveFeedback
        setPositiveFeedback(newFeedback)
        // Old behavior was to call onFeedback only when the feedback is positive
        if (newFeedback) {
            setNegativeFeedback(null)
            onFeedback(true)
        }
    }, [onFeedback, positiveFeedback])

    const handleNegativeFeedback = useCallback(() => {
        const newFeedback = !negativeFeedback
        setNegativeFeedback(newFeedback)
        // Old behavior was to call onFeedback only when the feedback is negative
        if (newFeedback) {
            setPositiveFeedback(null)
            onFeedback(false)
        }
    }, [onFeedback, negativeFeedback])
    const ariaLabelForPositiveFeedback = useMemo(() => {
        if (positiveFeedback) {
            return getAriaLabelForPressedState({
                title,
                feedbackType: 'positive'
            })
        } else {
            return getAriaLabelForUnpressedState({
                title,
                feedbackType: 'positive'
            })
        }
    }, [title, positiveFeedback])

    const ariaLabelForNegativeFeedback = useMemo(() => {
        if (negativeFeedback) {
            return getAriaLabelForPressedState({
                title,
                feedbackType: 'negative'
            })
        } else {
            return getAriaLabelForUnpressedState({
                title,
                feedbackType: 'negative'
            })
        }
    }, [title, negativeFeedback])

    return (
        <>
            <IconButton
                aria-pressed={!!positiveFeedback}
                aria-label={formatMessage(ariaLabelForPositiveFeedback, {
                    articleTitle: title
                })}
                variant={'contained'}
                color={positiveFeedback ? 'primary' : 'secondary'}
                onClick={handlePositiveFeedback}
            >
                {
                    <LeafIcon
                        icon={'thumbs-up'}
                        fontSize="small"
                        iconStyle={positiveFeedback ? 'solid' : 'regular'}
                    />
                }
            </IconButton>
            <IconButton
                aria-pressed={!!negativeFeedback}
                aria-label={formatMessage(ariaLabelForNegativeFeedback, {
                    articleTitle: title
                })}
                color={negativeFeedback ? 'primary' : 'secondary'}
                variant={'contained'}
                onClick={handleNegativeFeedback}
            >
                {
                    <LeafIcon
                        icon={'thumbs-down'}
                        fontSize="small"
                        iconStyle={negativeFeedback ? 'solid' : 'regular'}
                    />
                }
            </IconButton>
        </>
    )
}

export default React.memo(FeedbackActions)
